import React from 'react';
import { X } from 'lucide-react';
import {useTranslation} from "react-i18next";
import {useAvailabilityViewModel} from "../../view-model/professional/availability-view-model";
import {observer} from "mobx-react-lite";

interface DrawerProps {
  onClose: () => void;
  children: React.ReactNode;
  position?: 'right' | 'left';
  width?: string;
  disableDarkMode?: boolean;
  className?: string;
  hideSlotTypeSelector?: boolean;
  isOpen?: boolean;  // Si fourni, utilise cette prop au lieu de viewModel.getDrawerOpen
  isVisible?: boolean;  // Si fourni, utilise cette prop au lieu de viewModel.getIsVisibleDrawer
}

const Drawer: React.FC<DrawerProps> = observer(({
  onClose,
  children,
  position = 'right',
  width = '400px',
  disableDarkMode = false,
  className = '',
  hideSlotTypeSelector = false,
  isOpen,
  isVisible
}) => {
  const { t } = useTranslation();
  const viewModel = useAvailabilityViewModel();

  // Utiliser les props si fournies, sinon utiliser le viewModel
  const drawerOpen = isOpen !== undefined ? isOpen : viewModel.getDrawerOpen;
  const drawerVisible = isVisible !== undefined ? isVisible : viewModel.getIsVisibleDrawer;

  // Gérer la fermeture du drawer avec animation
  const handleClose = () => {
    // Appeler onClose directement (la logique de fermeture est dans le parent)
    onClose();
  };

  // Ne rien rendre si le drawer n'est pas ouvert
  if (!drawerOpen) return null;

  // Calculer les classes CSS pour l'animation
  const translateClass = position === 'right'
    ? (drawerVisible ? 'translate-x-0' : 'translate-x-full')
    : (drawerVisible ? 'translate-x-0' : '-translate-x-full');

  const overlayClass = drawerVisible ? 'opacity-100' : 'opacity-0';

  return (
    <>
      {/* Overlay avec animation */}
      <div 
        className={`fixed inset-0 bg-black/30 z-40 transition-opacity duration-300 ${overlayClass}`}
        onClick={handleClose}
      />
      
      {/* Drawer avec animation */}
      <div 
        className={`fixed inset-y-0 ${position === 'right' ? 'right-0' : 'left-0'} z-50 bg-white ${!disableDarkMode ? 'dark:bg-gray-750' : ''} shadow-xl transition-transform duration-300 transform p-0 flex flex-col w-full sm:w-auto ${translateClass} ${className || ''}`}
        style={{ maxWidth: '100%', width: width }}
      >
        {/* Header */}
        <div className={`flex justify-between items-center p-4 border-b ${!disableDarkMode ? 'dark:border-gray-700' : ''}`}>
          {!hideSlotTypeSelector ? (
            <div className="flex flex-col space-y-2">
              <h2 className="text-lg font-semibold">{t('availability.textOptionDrawer')}</h2>
              <div className="flex gap-4 items-center">
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="slot-type"
                    value="ponctuel"
                    className="sr-only peer"
                    checked={viewModel.getSlotType === 'ponctuel'}
                    onChange={() => viewModel.setSlotType('ponctuel')}
                  />
                  <div className="w-4 h-4 border rounded-full border-gray-300 peer-checked:border-4 peer-checked:border-primary"></div>
                  <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('availability.oneTimeSlots')}
                  </span>
                </label>

                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="slot-type"
                    value="recurrent"
                    className="sr-only peer"
                    checked={viewModel.getSlotType === 'recurrent'}
                    onChange={() => viewModel.setSlotType('recurrent')}
                  />
                  <div className="w-4 h-4 border rounded-full border-gray-300 peer-checked:border-4 peer-checked:border-primary"></div>
                  <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('availability.recurringSlots')}
                  </span>
                </label>
              </div>
            </div>
          ) : (
            <div className="flex-1" />
          )}
          <button
              onClick={handleClose}
              className={`p-1 rounded-full hover:bg-gray-100 ${!disableDarkMode ? 'dark:hover:bg-gray-700' : ''} transition-colors`}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className={`flex-1 overflow-auto p-4 ${disableDarkMode ? 'bg-white text-gray-900' : ''}`}>
          {children}
        </div>
      </div>
    </>
  );
});

export default Drawer;
